import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1776590976752-3a157f8d31dc?crop=entropy&cs=srgb&fm=jpg&q=85";

export const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100vh] pt-28 pb-24 overflow-hidden bg-radial-sky noise-overlay"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-xs tracking-[0.3em] uppercase text-[#1D4E89] font-medium mb-6"
            >
              RB InnerRise Coaching Academy · RBICA
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              data-testid="hero-headline"
              className="font-serif text-[#0B2B40] text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight font-light"
            >
              Rise from <span className="italic font-medium text-[#1D4E89]">within </span>
              and
              <span className="italic font-medium"> transform </span>
              your life.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-7 text-base md:text-lg text-[#1D4E89] tracking-[0.15em] font-medium uppercase"
            >
              Coaching · Wellness · Leadership · Education · Personal Growth
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-6 text-lg md:text-xl text-[#4A5568] font-light leading-relaxed max-w-xl"
            >
              Life Coach · Educator · Speaker. Helping people grow with
              confidence and purpose — guided by Reza Muhammad Behzad.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <a
                href="#discovery-call"
                data-testid="hero-cta-button"
                className="group bg-[#0B2B40] text-white hover:bg-[#1D4E89] px-9 py-4 text-sm font-medium tracking-wide transition-colors duration-300 inline-flex items-center justify-center gap-2"
              >
                Book a Free Discovery Call
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <a
                href="#programs"
                data-testid="hero-secondary-button"
                className="bg-transparent text-[#0B2B40] border border-[#0B2B40] hover:bg-[#E8F0F8] px-9 py-4 text-sm font-medium tracking-wide transition-colors duration-300 inline-flex items-center justify-center"
              >
                View Programs & Pricing
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.8 }}
              className="mt-14 flex flex-wrap items-center gap-x-10 gap-y-4 text-sm text-[#4A5568]"
            >
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#1D4E89]" />
                <span>Certified Life Coach</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#1D4E89]" />
                <span>Seattle Area · Online</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#1D4E89]" />
                <span>5 Languages</span>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.4 }}
              className="relative aspect-[4/5] w-full max-w-md mx-auto overflow-hidden"
            >
              <img
                src={HERO_IMG}
                alt="Calm ocean horizon"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0B2B40]/30 via-transparent to-white/10" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="hidden md:block absolute -bottom-8 -left-8 lg:-left-16 bg-white shadow-[0_24px_60px_rgba(11,43,64,0.12)] p-7 max-w-xs"
            >
              <p className="font-serif italic text-lg text-[#0B2B40] leading-snug">
                “Keep learning, keep growing, keep dreaming.”
              </p>
              <div className="mt-4 text-xs tracking-[0.25em] uppercase text-[#4A5568]">
                — Reza Muhammad Behzad
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
